import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { getFloatingButtonSize, FLOATING_BUTTON } from '../utils/responsiveConstants';
import { SELECTORS } from '../utils/domConstants';
import './FloatingThemeButton.css';

const FloatingThemeButton = () => {
  const { toggleTheme, isDark } = useTheme();
  const [isNearFooter, setIsNearFooter] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrollToTop, setIsScrollToTop] = useState(false);

  // Cache footer element reference - prevents repeated DOM queries
  const footerRef = useRef(null);

  useEffect(() => {
    // Cache footer element once on mount
    footerRef.current = document.querySelector(SELECTORS.FOOTER);

    const handleScroll = () => {
      // Calculate scroll progress
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

      setScrollProgress(progress);
      setIsScrollToTop(progress >= 90);

      // Footer detection using cached reference
      const footer = footerRef.current;
      if (!footer) return;

      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Get button size based on viewport using utility function
      const { size: buttonSize, margin } = getFloatingButtonSize();

      // Check if footer is visible and would overlap with button
      const footerTop = footerRect.top;
      const buttonBottomPosition = windowHeight - (buttonSize + margin);

      // Use constant for footer buffer
      setIsNearFooter(footerTop <= buttonBottomPosition + FLOATING_BUTTON.footerBuffer);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const handleButtonClick = () => {
    if (isScrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      toggleTheme();
    }
  };

  // Calculate circle properties for progress ring using responsive radius
  const { radius } = getFloatingButtonSize();
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

  return (
    <button
      className={`floating-theme-button ${isNearFooter ? 'near-footer' : ''}`}
      onClick={handleButtonClick}
      aria-label={isScrollToTop ? 'Scroll to top' : `Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      <svg className="progress-ring" width="100%" height="100%">
        <circle
          cx="50%"
          cy="50%"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
        />
      </svg>
      <span className={`theme-icon ${isScrollToTop ? 'arrow-active' : isDark ? 'sun-active' : 'moon-active'}`}>
        <svg className="sun-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <svg className="moon-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <svg className="up-arrow-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </span>
    </button>
  );
};

export default FloatingThemeButton;